export type TEventCallback = (arg0: Event) => void;

export class SubscriptionManager {
  private subscriptions: { dispose: () => void }[] = [];

  public subscribeToDomEvent(element: GlobalEventHandlers, eventName: string, callback: TEventCallback): void {
    element.addEventListener(eventName, callback);
    this.subscriptions.push({
      dispose: () => element.removeEventListener(eventName, callback),
    });
  }

  public subscribeToClickOutside(element: HTMLElement, callback: (event: Event) => void): void {
    this.subscribeToDomEvent(window, 'click', (e: Event) => {
      if (e.target instanceof Element && e.target.isConnected && !element.contains(e.target)) {
        callback(e);
      }
    });
  }

  public subscribeToResize(callback: () => void): void {
    window.addEventListener('resize', callback);

    this.subscriptions.push({
      dispose: () => window.removeEventListener('resize', callback),
    });
  }

  public subscribeToElementResize(element: HTMLElement, callback: () => void): void {
    const resizeObserver = new ResizeObserver(callback);
    resizeObserver.observe(element);

    this.subscriptions.push({
      dispose: () => resizeObserver.disconnect(),
    });
  }

  public subscribeToAncestorScrolls(element: HTMLElement, callback: () => void): void {
    const scrollableAncestors: (HTMLElement | Window)[] = [];
    let parent: HTMLElement | null = element.parentElement;

    const isScrollable = (el: HTMLElement): boolean => {
      const style = getComputedStyle(el);
      return (
        (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth) &&
        /(auto|scroll|overlay)/.test(style.overflow + style.overflowY + style.overflowX)
      );
    };

    while (parent && parent !== document.body) {
      if (isScrollable(parent)) {
        scrollableAncestors.push(parent);
      }
      parent = parent.parentElement;
    }
    scrollableAncestors.push(window);
    scrollableAncestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', callback);
      this.subscriptions.push({
        dispose: () => ancestor.removeEventListener('scroll', callback),
      });
    });
  }

  public disposeSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.dispose());
    this.subscriptions.length = 0;
  }
}
